import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/AdminLayout';
import AvatarUpload from '../../components/AvatarUpload';

export default function Mensagens() {
  const { alunas } = useAuth();
  const [alunaAtiva, setAlunaAtiva] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [recadoTexto, setRecadoTexto] = useState('');
  const [recadoDestino, setRecadoDestino] = useState('todos');
  const [enviandoRecado, setEnviandoRecado] = useState(false);
  const [recadoMsg, setRecadoMsg] = useState('');
  const [naoLidas, setNaoLidas] = useState({}); // { aluna_id: count }
  const [busca, setBusca] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Carregar contagem de não lidas por aluna
  useEffect(() => {
    carregarNaoLidas();
  }, []);

  async function carregarNaoLidas() {
    const { data } = await supabase
      .from('mensagens')
      .select('aluna_id')
      .eq('remetente', 'aluna')
      .eq('lida', false);
    if (!data) return;
    const contagem = {};
    data.forEach(m => { contagem[m.aluna_id] = (contagem[m.aluna_id] || 0) + 1; });
    setNaoLidas(contagem);
  }

  // Carregar mensagens da aluna ativa
  useEffect(() => {
    if (!alunaAtiva) return;
    carregarMensagens(alunaAtiva.id);
    marcarComoLidas(alunaAtiva.id);
  }, [alunaAtiva]);

  // Scroll automático
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  async function carregarMensagens(alunaId) {
    const { data } = await supabase
      .from('mensagens')
      .select('*')
      .eq('aluna_id', alunaId)
      .order('created_at', { ascending: true });
    setMensagens(data || []);
  }

  async function marcarComoLidas(alunaId) {
    await supabase
      .from('mensagens')
      .update({ lida: true })
      .eq('aluna_id', alunaId)
      .eq('remetente', 'aluna')
      .eq('lida', false);
    setNaoLidas(nl => { const n = { ...nl }; delete n[alunaId]; return n; });
  }

  async function enviar(e) {
    e.preventDefault();
    if (!texto.trim() || !alunaAtiva) return;
    setEnviando(true);
    const { data } = await supabase
      .from('mensagens')
      .insert({ aluna_id: alunaAtiva.id, remetente: 'admin', texto: texto.trim(), lida: true })
      .select()
      .single();
    if (data) setMensagens(m => [...m, data]);
    setTexto('');
    setEnviando(false);
    inputRef.current?.focus();
  }

  async function dispararPushRecado(destinos, textoLimpo) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return null;
      const res = await fetch('/.netlify/functions/send-push', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          alunaIds: destinos.map(aluna => aluna.id),
          title: 'Team Carol Macedo',
          body: textoLimpo,
          url: '/aluna/hoje',
        }),
      });
      return await res.json().catch(() => null);
    } catch {
      return null;
    }
  }

  async function inserirMensagens(rows) {
    const { error } = await supabase.from('mensagens').insert(rows.map(row => ({ ...row, tipo: 'recado_home' })));
    if (!error) return { error: null };
    if (String(error.message || '').toLowerCase().includes('tipo')) {
      return supabase.from('mensagens').insert(rows);
    }
    return { error };
  }

  async function enviarRecado(e) {
    e.preventDefault();
    const textoLimpo = recadoTexto.trim();
    if (!textoLimpo) return;
    const destinos = recadoDestino === 'todos'
      ? (alunas || []).filter(a => a.status !== 'pausada')
      : (alunas || []).filter(a => a.id === recadoDestino);
    if (destinos.length === 0) {
      setRecadoMsg('Escolha pelo menos uma aluna.');
      return;
    }
    setEnviandoRecado(true);
    setRecadoMsg('');
    const rows = destinos.map(aluna => ({
      aluna_id: aluna.id,
      remetente: 'admin',
      texto: textoLimpo,
      lida: false,
    }));
    const { error } = await inserirMensagens(rows);
    if (error) {
      setRecadoMsg('Não foi possível enviar o recado: ' + error.message);
    } else {
      const push = await dispararPushRecado(destinos, textoLimpo);
      setRecadoTexto('');
      const pushInfo = push?.sent > 0
        ? ` Push enviado para ${push.sent} dispositivo(s).`
        : (push?.error ? ' Push pendente: confira a configuração de notificações.' : ' Push ficará ativo para quem tocar em “Ativar notificações”.');
      setRecadoMsg((recadoDestino === 'todos' ? 'Recado enviado para todas as alunas ativas.' : 'Recado enviado para a tela inicial da aluna.') + pushInfo);
      if (alunaAtiva) carregarMensagens(alunaAtiva.id);
    }
    setEnviandoRecado(false);
  }

  const formatHora = (iso) => {
    const d = new Date(iso);
    const hoje = new Date();
    const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
    const mesmaData = d.toDateString() === hoje.toDateString();
    const eraOntem = d.toDateString() === ontem.toDateString();
    if (mesmaData) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (eraOntem) return `Ontem ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const alunasFiltradas = (alunas || [])
    .filter(a => a.nome?.toLowerCase().includes(busca.toLowerCase()) || a.name?.toLowerCase().includes(busca.toLowerCase()))
    .sort((a, b) => (naoLidas[b.id] || 0) - (naoLidas[a.id] || 0));

  const totalNaoLidas = Object.values(naoLidas).reduce((s, v) => s + v, 0);

  return (
    <AdminLayout>
      <style>{`
        .admin-msg-card {
          background: rgba(255, 255, 255, 0.82);
          border: 1px solid rgba(216, 202, 182, 0.72);
          border-radius: 28px;
          box-shadow: 0 14px 36px rgba(82, 69, 57, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }
        .admin-msg-layout {
          display: grid;
          grid-template-columns: 300px minmax(0, 1fr);
          gap: 16px;
          height: calc(100vh - 210px);
          min-height: 520px;
        }
        .admin-msg-input {
          border: 1.5px solid rgba(216, 202, 182, 0.9);
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.76);
          outline: none;
          transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
        }
        .admin-msg-input:focus {
          border-color: rgba(91, 64, 46, 0.42);
          box-shadow: 0 0 0 4px rgba(91, 64, 46, 0.07);
          background: #fff;
        }
        .admin-recado-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          align-items: end;
        }
        @media (max-width: 900px) {
          .admin-msg-layout {
            grid-template-columns: 1fr;
            height: auto;
            min-height: 0;
          }
          .admin-msg-list {
            max-height: 330px;
          }
          .admin-msg-chat {
            min-height: 560px;
          }
          .admin-recado-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: '34px', fontWeight: 700, color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          Mensagens
          {totalNaoLidas > 0 && (
            <span style={{ background: '#5B402E', color: 'white', borderRadius: '24px', padding: '2px 10px', fontSize: '13px', fontWeight: 700 }}>
              {totalNaoLidas}
            </span>
          )}
        </h1>
        <p style={{ color: '#8C7F70', margin: '4px 0 0', fontSize: '14px' }}>Troque mensagens com suas alunas</p>
      </div>

      <form onSubmit={enviarRecado} className="admin-msg-card" style={{ padding: '18px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#251C19' }}>Recado na tela inicial</div>
            <div style={{ fontSize: '12px', color: '#8C7F70', marginTop: '3px' }}>Aparece com destaque no Hoje da aluna. Pode ser individual ou para todas.</div>
          </div>
          <select
            value={recadoDestino}
            onChange={e => setRecadoDestino(e.target.value)}
            className="admin-msg-input"
            style={{ padding: '10px 14px', fontSize: '13px', color: '#251C19', minWidth: '210px' }}
          >
            <option value="todos">Todas as alunas ativas</option>
            {alunasFiltradas.map(aluna => <option key={aluna.id} value={aluna.id}>{aluna.name || aluna.nome}</option>)}
          </select>
        </div>
        <div className="admin-recado-grid">
          <textarea
            value={recadoTexto}
            onChange={e => setRecadoTexto(e.target.value)}
            placeholder="Ex: Já bebeu água hoje? Passa no app e marca sua rotina."
            rows={2}
            className="admin-msg-input"
            style={{ width: '100%', padding: '12px 15px', fontSize: '14px', resize: 'vertical', fontFamily: 'Inter, sans-serif', boxSizing: 'border-box' }}
          />
          <button
            type="submit"
            disabled={!recadoTexto.trim() || enviandoRecado}
            style={{ padding: '13px 20px', background: recadoTexto.trim() ? '#5B402E' : '#D8CAB6', color: 'white', border: 'none', borderRadius: '22px', fontSize: '13px', fontWeight: 700, cursor: recadoTexto.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', boxShadow: recadoTexto.trim() ? '0 10px 22px rgba(91,64,46,.18)' : 'none' }}
          >
            {enviandoRecado ? 'Enviando...' : 'Enviar recado'}
          </button>
        </div>
        {recadoMsg && <div style={{ marginTop: '10px', fontSize: '12px', color: recadoMsg.includes('Não foi') ? '#C0392B' : '#5C6F3B' }}>{recadoMsg}</div>}
      </form>

      <div className="admin-msg-layout">

        {/* Lista de alunas */}
        <div className="admin-msg-card admin-msg-list" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #E8E0D0' }}>
            <input
              type="text"
              placeholder="Buscar aluna..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              className="admin-msg-input"
              style={{ width: '100%', padding: '9px 13px', fontSize: '13px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {alunasFiltradas.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#8C7F70', fontSize: '13px' }}>Nenhuma aluna</div>
            ) : alunasFiltradas.map(aluna => {
              const nome = aluna.name || aluna.nome || '';
              const nl = naoLidas[aluna.id] || 0;
              const ativa = alunaAtiva?.id === aluna.id;
              return (
                <div
                  key={aluna.id}
                  onClick={() => setAlunaAtiva(aluna)}
                  style={{
                    padding: '12px 14px',
                    display: 'flex', alignItems: 'center', gap: '10px',
                    cursor: 'pointer',
                    background: ativa ? 'rgba(137,91,64,0.09)' : 'transparent',
                    borderLeft: ativa ? '3px solid #5B402E' : '3px solid transparent',
                    borderBottom: '1px solid #F5F0E8',
                    transition: 'all 0.15s',
                  }}
                >
                  <AvatarUpload userId={aluna.id} nome={nome} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: nl > 0 ? 700 : 500, color: '#251C19', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nome}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8C7F70' }}>{aluna.status === 'ativa' ? 'Ativa' : 'Pausada'}</div>
                  </div>
                  {nl > 0 && (
                    <span style={{ background: '#5B402E', color: 'white', borderRadius: '24px', padding: '1px 7px', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
                      {nl}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Área do chat */}
        <div className="admin-msg-card admin-msg-chat" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!alunaAtiva ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#8C7F70', gap: '12px' }}>
              <div style={{ width: 58, height: 58, borderRadius: 20, display: 'grid', placeItems: 'center', background: 'rgba(91,64,46,.1)', color: '#5B402E', fontSize: '16px', fontWeight: 800 }}>TC</div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#251C19' }}>Selecione uma aluna</div>
              <div style={{ fontSize: '13px' }}>Escolha uma conversa na lista ao lado</div>
            </div>
          ) : (
            <>
              {/* Header do chat */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(216,202,182,.65)', display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(250,247,242,.72)', backdropFilter: 'blur(14px)' }}>
                <AvatarUpload userId={alunaAtiva.id} nome={alunaAtiva.name || alunaAtiva.nome || ''} size={40} />
                <div>
                  <div style={{ fontWeight: 600, color: '#251C19', fontSize: '15px' }}>{alunaAtiva.name || alunaAtiva.nome}</div>
                  <div style={{ fontSize: '12px', color: '#8C7F70' }}>Conversa direta</div>
                </div>
              </div>

              {/* Mensagens */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '9px', background: 'linear-gradient(180deg, rgba(255,255,255,.24), rgba(250,247,242,.32))' }}>
                {mensagens.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#8C7F70', fontSize: '13px', marginTop: '40px' }}>
                    Nenhuma mensagem ainda. Inicie a conversa.
                  </div>
                ) : mensagens.map((msg, i) => {
                  const isAdmin = msg.remetente === 'admin';
                  return (
                    <div key={msg.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '70%',
                        padding: '10px 14px',
                        borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: isAdmin ? 'linear-gradient(135deg, #3A2E27, #251C19)' : '#F5F0E8',
                        color: isAdmin ? '#FAF7F2' : '#251C19',
                        fontSize: '14px',
                        lineHeight: 1.5,
                        boxShadow: isAdmin ? '0 10px 20px rgba(37,28,25,.12)' : '0 8px 16px rgba(82,69,57,.05)',
                      }}>
                        <div>{msg.texto}</div>
                        <div style={{ fontSize: '10px', opacity: 0.55, marginTop: '4px', textAlign: 'right' }}>
                          {formatHora(msg.created_at)}
                          {isAdmin && <span style={{ marginLeft: '4px' }}>{msg.lida ? '✓✓' : '✓'}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <form onSubmit={enviar} style={{ padding: '14px 16px', borderTop: '1px solid rgba(216,202,182,.65)', display: 'flex', gap: '10px', alignItems: 'center', background: 'rgba(255,255,255,.72)', backdropFilter: 'blur(14px)' }}>
                <textarea
                  ref={inputRef}
                  value={texto}
                  onChange={e => setTexto(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(e); } }}
                  placeholder="Digite uma mensagem... (Enter para enviar)"
                  rows={1}
                  style={{
                    flex: 1,
                    padding: '11px 14px', fontSize: '14px', resize: 'none',
                    fontFamily: 'Inter, sans-serif',
                    maxHeight: '100px', overflowY: 'auto',
                  }}
                  className="admin-msg-input"
                />
                <button
                  type="submit"
                  disabled={!texto.trim() || enviando}
                  style={{
                    width: 46, height: 46, display: 'grid', placeItems: 'center', flexShrink: 0,
                    background: texto.trim() ? '#251C19' : '#D8CAB6',
                    color: 'white', border: 'none', borderRadius: '24px',
                    fontSize: '17px', fontWeight: 700, cursor: texto.trim() ? 'pointer' : 'not-allowed',
                    transition: 'background 0.2s, transform .2s', whiteSpace: 'nowrap',
                    boxShadow: texto.trim() ? '0 10px 22px rgba(37,28,25,.16)' : 'none',
                  }}
                  aria-label="Enviar mensagem"
                >
                  {enviando ? '...' : '➤'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
